import React, { useState, useRef, useEffect } from 'react'
import { GlobeIcon, HomeIcon, UsersIcon, DocumentIcon, ExternalLinkIcon } from '../icons/icons'

export interface BizFirstLinksProps {
  className?: string
  style?: React.CSSProperties
}

/**
 * BizFirstLinks Component
 *
 * A compact header panel that links to BizFirst community resources.
 * Displayed as a globe icon that toggles a dropdown menu.
 *
 * Links to:
 * - BizFirstAi.com (main site)
 * - community.BizFirstAi.com (community)
 * - docs.BizFirstAi.com (docs)
 */
export function BizFirstLinks({ className = '', style = {} }: BizFirstLinksProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && panelRef.current) {
        if (!buttonRef.current.contains(event.target as Node) && !panelRef.current.contains(event.target as Node)) {
          setIsOpen(false)
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
    return undefined
  }, [isOpen])

  const links = [
    {
      label: 'BizFirstAi.com',
      url: 'https://bizfirstai.com',
      icon: <HomeIcon size={18} />
    },
    {
      label: 'Community',
      url: 'https://community.bizfirstai.com',
      icon: <UsersIcon size={18} />
    },
    {
      label: 'Docs',
      url: 'https://docs.bizfirstai.com',
      icon: <DocumentIcon size={18} />
    },
  ]

  return (
    <div className={`bizfirst-links ${className}`} style={style}>
      <button
        ref={buttonRef}
        className="bizfirst-links-button"
        onClick={() => setIsOpen(!isOpen)}
        title="BizFirst Resources"
        aria-label="Toggle BizFirst resources menu"
        aria-expanded={isOpen}
      >
        <GlobeIcon size={20} />
      </button>

      {isOpen && (
        <div ref={panelRef} className="bizfirst-links-panel">
          {links.map((link) => (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bizfirst-links-item"
            >
              <span className="bizfirst-links-icon">{link.icon}</span>
              <span className="bizfirst-links-label">{link.label}</span>
              <ExternalLinkIcon size={14} className="bizfirst-links-arrow" />
            </a>
          ))}
        </div>
      )}

      <style>{`
        .bizfirst-links {
          position: relative;
          display: flex;
          align-items: center;
        }

        .bizfirst-links-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          padding: 0;
          background: var(--bg-raised);
          border: 1px solid var(--border-default); box-sizing: border-box;
          border-radius: 6px;
          color: var(--text-primary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .bizfirst-links-button:hover {
          background: var(--border-default);
        }

        .bizfirst-links-button svg {
          width: 20px;
          height: 20px;
        }

        .bizfirst-links-panel {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: var(--color-bg-secondary, var(--bg-secondary, #1c1f26));
          border: 1px solid var(--color-border, var(--border, #30363d));
          border-radius: 8px;
          box-shadow: 0 10px 25px rgb(var(--black-rgb) / 0.4);
          z-index: 1000;
          min-width: 200px;
          animation: slideDown 0.2s ease;
        }

        .bizfirst-links-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          color: var(--color-text-secondary, var(--text-secondary, #6B7879));
          text-decoration: none;
          transition: all 0.2s ease;
          border-bottom: 1px solid var(--border-default);
        }

        .bizfirst-links-item:last-child {
          border-bottom: none;
        }

        .bizfirst-links-item:hover {
          background: var(--color-bg-tertiary, var(--bg-tertiary, #30363d));
          color: var(--color-primary, var(--obs-primary, #309B48));
        }

        .bizfirst-links-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          color: currentColor;
        }

        .bizfirst-links-icon svg {
          width: 18px;
          height: 18px;
        }

        .bizfirst-links-label {
          flex: 1;
          font-size: 14px;
          font-weight: 500;
        }

        .bizfirst-links-arrow {
          flex-shrink: 0;
          opacity: 0.6;
          transition: transform 0.2s ease;
        }

        .bizfirst-links-item:hover .bizfirst-links-arrow {
          transform: translate(2px, -2px);
          opacity: 1;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  )
}

export default BizFirstLinks
